// Phase 3A — the persistent "Continue" thread.
// Phase 3B — now memory-aware: an unfinished experience outranks a generic CTA.
//
// One quiet, dismissible pill that always offers the single next step for the
// current visitor. It never blocks content, never opens a modal, never
// animates aggressively, and stays dismissed for the rest of the session.
import { useEffect, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { ArrowRight, X } from "lucide-react";
import { useJourney } from "@/hooks/use-journey";
import { useReturnLoop } from "@/hooks/use-return-loop";
import { getCookieConsent } from "@/components/site/CookieConsent";
import { usePlayer } from "@/lib/player/PlayerContext";
import { primaryAction } from "@/lib/journey";
import { continueOffsetClass, shouldShowContinue } from "@/lib/continue-surface";
import { track } from "@/lib/analytics";

const DISMISS_KEY = "ka.journey.continue.dismissed.v1";


/** Surfaces where a journey nudge would be noise or a conflict. */
const MUTED_PREFIXES = ["/admin", "/auth", "/checkout", "/press", "/debug", "/api"];

interface PillProps {
  eyebrow: string;
  label: string;
  to: string;
  params?: Record<string, string>;
  onNavigate: () => void;
  onDismiss: () => void;
  onView?: () => void;
  /** Vertical offset, raised when the mini player occupies the bottom rail. */
  offsetClass: string;
}

function ContinuePill({ eyebrow, label, to, params, onNavigate, onDismiss, onView, offsetClass }: PillProps) {
  useEffect(() => {
    onView?.();
    // Fires once per mounted continuation surface.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  return (
    <div className={`pointer-events-none fixed inset-x-0 z-30 flex justify-center px-4 lg:justify-end lg:pr-6 ${offsetClass}`}>

      <div className="pointer-events-auto flex max-w-md items-center gap-1 rounded-full border hairline bg-obsidian/90 pl-1 pr-1 shadow-gold backdrop-blur">
        <Link
          to={to as never}
          params={params as never}
          onClick={onNavigate}
          className="flex min-h-11 items-center gap-2 rounded-full px-4 text-sm text-gold-luminous transition hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-gold/70">{eyebrow}</span>
          <span className="truncate">{label}</span>
          <ArrowRight size={14} aria-hidden />
        </Link>
        <button
          type="button"
          aria-label="Dismiss continue prompt"
          onClick={onDismiss}
          className="flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          <X size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}

export function ContinueThread() {
  const { pathname } = useLocation();
  const journey = useJourney();
  const loop = useReturnLoop();
  const { current: nowPlaying } = usePlayer();
  const [dismissed, setDismissed] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [consentPending, setConsentPending] = useState(true);

  useEffect(() => {
    try {
      setDismissed(window.sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  // Never stack the pill under the cookie banner: wait until consent resolves.
  useEffect(() => {
    setConsentPending(!getCookieConsent());
    const onConsent = () => setConsentPending(false);
    window.addEventListener("ka:cookie-consent", onConsent);
    return () => window.removeEventListener("ka:cookie-consent", onConsent);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* private mode */
    }
  };

  const visible = shouldShowContinue({
    journeyLoading: journey.loading,
    dismissed,
    scrolled,
    consentPending,
    pathname,
    mutedPrefixes: MUTED_PREFIXES,
  });
  if (!visible) return null;

  // Clear the mini player's bottom rail when audio is active.
  const offsetClass = continueOffsetClass(Boolean(nowPlaying));

  // Phase 3B — a remembered, unfinished experience is the kindest next step.
  const resume = loop.ready ? loop.continuation(pathname) : null;
  if (resume) {
    return (
      <ContinuePill
        eyebrow="Pick up where you left off"
        label={resume.title}
        to={resume.href}
        offsetClass={offsetClass}
        onView={() => track("return_continue_view", { kind: resume.kind, ref: resume.ref })}
        onNavigate={() => track("return_continue_click", { kind: resume.kind, ref: resume.ref })}
        onDismiss={dismiss}
      />
    );
  }


  const action = primaryAction(journey.visitor);
  // Don't nudge someone toward the page they are already reading.
  const resolved = action.params
    ? action.to.replace(/\$(\w+)/g, (_m, k: string) => action.params?.[k] ?? "")
    : action.to;
  if (pathname === resolved) return null;

  return (
    <ContinuePill
      eyebrow="Continue"
      label={action.label}
      to={action.to}
      params={action.params}
      offsetClass={offsetClass}

      onNavigate={() =>
        track("journey_primary_cta", {
          action: action.id,
          stage: action.stage,
          surface: "continue_thread",
        })
      }
      onDismiss={dismiss}
    />
  );
}
